/* src/ui/format.ts — the one place a number becomes a string for a human (CLAUDE.md Money).
 *
 * Exactly one module formats currency. That is a correctness rule, not a style preference: the
 * moment two call sites round independently, the HUD and the cart window disagree about the
 * same balance by a cent and the player is left to guess which one is lying.
 */

import type { Money } from '../core/types.ts';

/*
 * Intl.NumberFormat is expensive to construct and cheap to reuse, and the money formatter runs
 * on every interpolated frame — building one per call would allocate several times a second
 * for no reason.
 */
const MONEY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const RATE = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  signDisplay: 'always',
});

const COUNT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

/** `$1,234.56`. The canonical rendering of a balance. */
export function formatMoney(value: Money | number): string {
  return MONEY.format(value);
}

/**
 * `+$12.34/min`, or `−$3.00/min` when the business is losing money. The sign is always shown:
 * a net rate is the number the player watches to decide whether a change helped, and an
 * unsigned "$3.00/min" reads as income when it may be a loss.
 */
export function formatRate(perMin: Money | number): string {
  return `${RATE.format(perMin)}/min`;
}

/** Whole units — servings, customers, counts. Never money. */
export function formatCount(value: number): string {
  return COUNT.format(value);
}

/** One decimal, for rates that are not money (`12.3/min` customers). */
export function formatPerMin(value: number): string {
  return `${value.toFixed(1)}/min`;
}
